import React, { useState, useEffect } from 'react';
import '../styles/ConstructionDocCreationModal.css';

function ConstructionDocCreationModal({
  isOpen,
  onClose,
  onCreateBlank,
  onGenerateFromScans,
  structures,
}) {
  const [isAutoGenerate, setIsAutoGenerate] = useState(true);
  const [selectedStructures, setSelectedStructures] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setIsAutoGenerate(true);
      setSelectedStructures([]);
      setIsGenerating(false);
      setProgress(0);
    }
  }, [isOpen]);

  const handleToggleStructure = (structureId) => {
    setSelectedStructures((prev) => {
      if (prev.includes(structureId)) {
        return prev.filter((id) => id !== structureId);
      } else {
        return [...prev, structureId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedStructures(structures.map((s) => s.id));
  };

  const handleDeselectAll = () => {
    setSelectedStructures([]);
  };

  const handleGenerate = async () => {
    if (!isAutoGenerate) {
      onCreateBlank();
      return;
    }

    if (selectedStructures.length === 0) {
      alert(
        'Please select at least one scan to generate construction documents.'
      );
      return;
    }

    setIsGenerating(true);
    setProgress(0);

    try {
      // Call the generate function with progress callback
      await onGenerateFromScans(selectedStructures, (progressPercent) => {
        setProgress(progressPercent);
      });
      // Modal will be closed by parent after successful generation
    } catch (error) {
      console.error('Error generating construction docs:', error);
      setIsGenerating(false);
      setProgress(0);
      // Error handling will be done by parent
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Construction Document</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Generation Type Selection */}
          <div className="generation-type-selector">
            <button
              className={`type-button ${isAutoGenerate ? 'active' : ''}`}
              onClick={() => setIsAutoGenerate(true)}
            >
              <div className="type-icon">📋</div>
              <div className="type-label">Generate from Scans</div>
              <div className="type-description">
                Auto-generate pages with schedules and room details
              </div>
            </button>
            <button
              className={`type-button ${!isAutoGenerate ? 'active' : ''}`}
              onClick={() => setIsAutoGenerate(false)}
            >
              <div className="type-icon">📄</div>
              <div className="type-label">Create Blank Document</div>
              <div className="type-description">
                Start with empty 24"×36" pages
              </div>
            </button>
          </div>

          {/* Scan Selection (only shown for auto-generate) */}
          {isAutoGenerate && (
            <div className="scan-selection-section">
              <div className="scan-selection-header">
                <h3>Select Scans to Include</h3>
                <div className="select-buttons">
                  <button
                    className="select-all-button"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <button
                    className="deselect-all-button"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="scan-list">
                {structures.length === 0 ? (
                  <p className="no-scans-message">
                    No scans available. Please create a scan first.
                  </p>
                ) : (
                  structures.map((structure) => (
                    <label key={structure.id} className="scan-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedStructures.includes(structure.id)}
                        onChange={() => handleToggleStructure(structure.id)}
                      />
                      <div className="scan-info">
                        <span className="scan-name">
                          {structure.name || 'Untitled Scan'}
                        </span>
                        <span className="scan-metadata">
                          {structure.createdAt &&
                            new Date(
                              structure.createdAt
                            ).toLocaleDateString()}{' '}
                          • {structure.rooms?.length || 0} rooms
                        </span>
                      </div>
                    </label>
                  ))
                )}
              </div>
              <div className="selection-summary">
                {selectedStructures.length} scan(s) selected
              </div>
            </div>
          )}

          {/* Progress Bar (shown during generation) */}
          {isGenerating && (
            <div className="progress-section">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="progress-text">{Math.round(progress)}%</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button
            className="generate-button"
            onClick={handleGenerate}
            disabled={
              isGenerating ||
              (isAutoGenerate && selectedStructures.length === 0)
            }
          >
            {isGenerating
              ? 'Generating...'
              : isAutoGenerate
                ? 'Generate'
                : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConstructionDocCreationModal;
